import { useCallback, useMemo } from "react";
import { notifyError } from "../../../../utils/notify";
import { notifyServiceBookingError } from "../../../../utils/serviceAvailabilityError";
import type { SalonService } from "../../../../types/salon";
import type {
  UpdateWaitlistEntryRequest,
  WaitlistEntry,
} from "../../../../types/waitlist";
import type { WaitlistStore } from "./useWaitlist";
import type { WalkInFormTarget, WalkInFormValues } from "./WalkInForm";
import {
  addedToast,
  entryClientName,
  removedToast,
  updatedToast,
  type WaitlistToast,
} from "./waitlistModel";
import {
  assignStartIso,
  assignedToast,
  resolveChoice,
  serviceMinutesFor,
  staffServiceIdFor,
  type AssignChoice,
  type AssignMode,
} from "./assignModel";
import { qualifiedFor } from "./staffAvailability";
import type { StaffAvailabilityNow } from "./useStaffAvailability";

export interface WaitlistActionsOptions {
  timezone: string;
  services: SalonService[];
  /** A walk-in was booked: put the new appointment on the grid. */
  onAssigned?: (transactionUuid: string) => void;
  /** SalonContext.refreshServices (SERVICE_NO_LONGER_AVAILABLE on assign). */
  refreshServices?: () => Promise<unknown> | void;
}

/** Availability at the moment of the pick (useStaffAvailability). */
export type AssignContext = StaffAvailabilityNow;

/**
 * The waitlist writes with their toasts (prototype copy) and error
 * notifications, shared by the desktop drawer and the mobile sheet. `toast`
 * is the surface's success toast (desktop notify / MobileToast).
 */
export function useWaitlistActions(
  store: WaitlistStore,
  toast: (copy: WaitlistToast) => void,
  { timezone, services, onAssigned, refreshServices }: WaitlistActionsOptions,
) {
  const {
    create,
    update,
    remove: removeEntry,
    reorder: reorderEntries,
    assign: assignEntry,
    refetch,
  } = store;

  /**
   * Book a waiting walk-in: "any" = a random provider who performs the
   * service and has room for it now; start = the current minute, or the
   * busy provider's first gap that fits it ("after"). The backend gets a concrete
   * staff_service_id + start_dt, and "any" carries over as
   * booked_using_any_available. True = booked.
   */
  const assign = useCallback(
    async (
      entry: WaitlistEntry,
      choice: AssignChoice,
      mode: AssignMode,
      ctx: AssignContext,
    ): Promise<boolean> => {
      const service = services.find((s) => s.id === entry.salon_service_uuid);
      const who = resolveChoice(
        choice,
        qualifiedFor(ctx.rows, service, ctx.nowWall),
      );
      if (!who) {
        notifyError("No one who offers this service is available right now");
        return false;
      }
      const staffServiceId = staffServiceIdFor(service, who.id);
      if (!staffServiceId) {
        notifyError(`${who.name} doesn’t offer ${entry.service_name}`);
        return false;
      }
      const effective: AssignMode = who.available ? "now" : mode;
      const startIso = assignStartIso(effective, who, ctx.nowMs, timezone);
      try {
        const response = await assignEntry(entry.uuid, {
          staff_service_id: staffServiceId,
          start_dt: startIso,
          booked_using_any_available: choice === "any",
        });
        toast(
          assignedToast({
            client: entryClientName(entry),
            staff: who.name,
            service: entry.service_name,
            startIso,
            minutes: serviceMinutesFor(
              service,
              who.id,
              entry.service_duration_mins,
            ),
            timezone,
            after:
              effective === "after" && who.freeAt ? who.afterLabel : undefined,
          }),
        );
        if (response?.transaction_uuid) onAssigned?.(response.transaction_uuid);
        return true;
      } catch (error) {
        console.error("Error assigning walk-in:", error);
        // 409 SERVICE_NO_LONGER_AVAILABLE → refresh services; other 400 /
        // 409s (row no longer waiting, staff service mismatch) show the
        // backend message, and the list is refetched.
        notifyServiceBookingError(error, {
          refreshServices,
          fallbackMessage: "Couldn’t assign the walk-in",
        });
        if ((error as { status?: number } | null)?.status === 409)
          void refetch();
        return false;
      }
    },
    [
      assignEntry,
      services,
      timezone,
      toast,
      onAssigned,
      refreshServices,
      refetch,
    ],
  );

  /**
   * New → end of the line (then booked when the form picked a provider);
   * Edit → only the changed fields. True = the form can close.
   */
  const save = useCallback(
    async (
      target: WalkInFormTarget,
      values: WalkInFormValues,
      ctx?: AssignContext,
    ): Promise<boolean> => {
      try {
        if (target.mode === "new") {
          const { entry, position } = await create({
            client_uuid: values.clientUuid,
            salon_service_uuid: values.serviceId,
            ...(values.comments ? { comments: values.comments } : {}),
          });
          if (values.assign && ctx) {
            // On the waitlist either way; a failed assign leaves it there.
            await assign(entry, values.assign.choice, values.assign.mode, ctx);
            return true;
          }
          toast(addedToast(values.clientName, values.serviceName, position));
          return true;
        }
        const e = target.entry;
        const patch: UpdateWaitlistEntryRequest = {};
        if (values.clientUuid !== e.client_uuid)
          patch.client_uuid = values.clientUuid;
        if (values.serviceId !== e.salon_service_uuid)
          patch.salon_service_uuid = values.serviceId;
        if (values.comments !== (e.comments ?? "").trim())
          patch.comments = values.comments;
        if (Object.keys(patch).length > 0) {
          await update(e.uuid, patch);
          toast(updatedToast(values.clientName, values.serviceName));
        }
        return true;
      } catch (error) {
        console.error("Error saving walk-in:", error);
        notifyError(
          target.mode === "new"
            ? "Couldn’t add the walk-in"
            : "Couldn’t update the walk-in",
        );
        return false;
      }
    },
    [create, update, assign, toast],
  );

  const remove = useCallback(
    async (entry: WaitlistEntry) => {
      try {
        await removeEntry(entry.uuid);
        toast(removedToast(entryClientName(entry), entry.service_name));
      } catch (error) {
        console.error("Error removing walk-in:", error);
        // 409 = already assigned on another device: show the fresh list.
        if ((error as { status?: number } | null)?.status === 409) {
          notifyError(`${entryClientName(entry)} was already assigned`);
          void refetch();
          return;
        }
        notifyError("Couldn’t remove the walk-in");
      }
    },
    [removeEntry, toast, refetch],
  );

  /** Optimistic (the store rolls back on failure). */
  const reorder = useCallback(
    async (ids: string[]) => {
      try {
        await reorderEntries(ids);
      } catch (error) {
        console.error("Error reordering the waitlist:", error);
        notifyError("Couldn’t reorder the waitlist");
      }
    },
    [reorderEntries],
  );

  return useMemo(
    () => ({ save, assign, remove, reorder }),
    [save, assign, remove, reorder],
  );
}

export type WaitlistActions = ReturnType<typeof useWaitlistActions>;
